import { computed, ref, toValue, watch, useApm, onApmReady, toError, useElementVisibility, useEventListener, usePreferredReducedMotion } from '#imports'
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'
import type { apm } from '@elastic/apm-rum'
import type { ApmLabels } from './useApmLabels'

export type VideoAutoplayOutcome = 'allowed' | 'blocked' | 'aborted' | 'failed'

/**
 * The first attempt's outcome, `recovered` once a tap starts a video that was blocked or
 * skipped, or `skipped` when reduced motion held it on its poster.
 */
export type VideoAutoplayState = VideoAutoplayOutcome | 'recovered' | 'skipped'

export interface UseDeferredVideoAutoplayOptions {
    /**
     * Reports whether the first autoplay attempt was allowed as `labels.video_autoplay`
     * (`skipped` when `respectReducedMotion` held it back), with this value as
     * `labels.video_autoplay_source`, and a later tap that starts a
     * blocked video as `labels.video_autoplay_recovered`. Set it on one hero video per
     * page, not on grids of tiles, so every page view counts once.
     */
    reportAs?: string
    /**
     * Keeps the video on its poster for visitors who prefer reduced motion until `play()`
     * is called. A tap elsewhere on the page does not start it, and the attempt is reported as
     * `skipped`.
     */
    respectReducedMotion?: boolean
    /**
     * Starts the video with sound where the browser allows audible autoplay and muted elsewhere, so
     * it never waits on its poster for a tap that muted playback would not need. Leave the `muted`
     * attribute off the element. A video that had to start muted turns its sound on at the visitor's
     * first tap anywhere on the page, which is the gesture audio needs, unless `setMuted` settled the
     * sound first; a tap on a `VideoSoundButton` is left to the button. `muted` tells the current
     * state. The first attempt reports which it got as `labels.video_autoplay_sound`.
     */
    sound?: boolean
    /**
     * Waits with the first attempt until this is true, for a hero whose intro board covers the video:
     * the film then starts, with its sound, as the board fades instead of running under it. Give the
     * element `preload="auto"` so it buffers meanwhile and the start is instant. A tap before then
     * starts nothing and leaves the recovery in place; the attempt still runs outside a gesture once
     * `ready` turns true, audible where the browser lets the page's earlier tap count for autoplay, else
     * muted until the next tap as usual. The attempt is reported from the moment `ready` turns true.
     */
    ready?: MaybeRefOrGetter<boolean>
}

export interface UseDeferredVideoAutoplayReturn {
    /** null until the first attempt settles */
    state: Readonly<Ref<VideoAutoplayState | null>>
    /** The video sits on its poster waiting for the visitor; show a play button. */
    awaitingTap: ComputedRef<boolean>
    /** Starts the video. Call it from a user gesture, which is what lifts the autoplay block. */
    play: () => void
    /** Whether the video plays without sound. Always true unless `sound` was requested. */
    muted: Readonly<Ref<boolean>>
    /**
     * Mutes or unmutes the video and settles the sound, so a later tap leaves it as chosen. Unmuting
     * must come from a user gesture, which is what lets audio start.
     */
    setMuted: (value: boolean) => void
}

export const useDeferredVideoAutoplay = (
    video: Ref<HTMLVideoElement | null | undefined>,
    options: UseDeferredVideoAutoplayOptions = {},
): UseDeferredVideoAutoplayReturn => {
    const apm = useApm()
    const visible = useElementVisibility(video)
    const reducedMotion = usePreferredReducedMotion()
    const ready = computed(() => options.ready === undefined || toValue(options.ready))
    const report = options.reportAs ? createApmReporter(options.reportAs) : undefined
    const state = ref<VideoAutoplayState | null>(null)
    const playing = ref(false)
    const muted = ref(true)
    let attempted = false
    // A video that had to start muted waits for the tap that lets its sound on.
    let soundPending = false

    const heldBack = () => Boolean(options.respectReducedMotion) && reducedMotion.value === 'reduce' && state.value !== 'recovered'

    // A later attempt can start a blocked video without a tap, e.g. once Low Power Mode is off.
    useEventListener(video, 'play', () => {
        playing.value = true
    })
    useEventListener(video, 'pause', () => {
        playing.value = false
    })

    watch(
        [video, visible, ready],
        ([el, isVisible, isReady]) => {
            if (!el) return

            if (isVisible) {
                if (!isReady || !hasSource(el)) return

                if (heldBack()) {
                    // The hold settles the first attempt, so a video later started from play() is not
                    // taken for a first autoplay when it scrolls back into view, which would overwrite
                    // the state and bring the hold back.
                    if (!attempted) {
                        attempted = true
                        state.value = 'skipped'
                        report?.({ video_autoplay: 'skipped' }, performance.now())
                    }
                    return
                }

                const isFirstAttempt = !attempted
                attempted = true
                const attemptedAt = performance.now()
                let reported = false
                const settle = (outcome: VideoAutoplayOutcome) => {
                    if (!isFirstAttempt || reported) return
                    reported = true
                    state.value = outcome
                    const labels: ApmLabels = { video_autoplay: outcome }
                    if (options.sound) labels.video_autoplay_sound = el.muted ? 'blocked' : 'allowed'
                    report?.(labels, attemptedAt)
                }

                const playback = options.sound ? playWithSound(el) : el.play()

                if (!el.paused) settle('allowed')

                playback
                    .then(() => settle('allowed'))
                    .catch((error: unknown) => {
                        const outcome = outcomeOf(error)
                        settle(outcome)
                        if (outcome === 'failed') apm.captureError(toError(error))
                    })
            }
            else if (!el.paused) {
                el.pause()
            }
        },
        { immediate: true },
    )

    // iOS Low Power Mode blocks the initial play() — recover on the next user tap. A video that had to
    // start muted takes the same tap to turn its sound on.
    // pointerdown does not grant user activation per the HTML spec; must be pointerup.
    const stopRecovery = useEventListener(
        'pointerup',
        (event: PointerEvent) => {
            const el = video.value

            // A tap before `ready` starts nothing: the attempt runs once `ready` turns true, and a browser
            // that lets the tap count for autoplay makes that attempt audible by itself.
            if (!el || !visible.value || !ready.value || heldBack()) return

            if (soundPending) {
                // The sound button toggles on the click that follows, which would mute again what this turned on.
                if (!isSoundButton(event.target)) setMuted(false)
                return
            }

            if (!el.paused) {
                stopRecovery()
                return
            }

            play()
        },
        { capture: true, passive: true },
    )

    // Audible autoplay needs an earlier interaction with the site, which a first visit never has, so a
    // blocked attempt falls back to muted playback, which browsers allow, rather than to the poster.
    function playWithSound(el: HTMLVideoElement): Promise<void> {
        el.muted = false

        return el.play().then(
            () => {
                muted.value = false
            },
            (error: unknown) => {
                if (outcomeOf(error) !== 'blocked') throw error
                el.muted = true
                muted.value = true
                soundPending = true

                return el.play()
            },
        )
    }

    function setMuted(value: boolean): void {
        soundPending = false
        muted.value = value
        const el = video.value

        if (!el) return

        el.muted = value
        // A video that stopped while muted restarts inside the same gesture that turns the sound on.
        if (!value && el.paused) play()
    }

    function play(): void {
        const el = video.value

        if (!el || !hasSource(el) || !el.paused) return

        const attemptedAt = performance.now()
        el.play()
            .then(() => {
                stopRecovery()
                if (state.value === 'blocked') report?.({ video_autoplay_recovered: true }, attemptedAt)
                if (state.value === 'blocked' || state.value === 'skipped') state.value = 'recovered'
            })
            .catch(() => {})
    }

    const awaitingTap = computed(() => (state.value === 'blocked' || state.value === 'skipped') && !playing.value)

    return { state, awaitingTap, play, muted, setMuted }
}

function outcomeOf(error: unknown): Exclude<VideoAutoplayOutcome, 'allowed'> {
    if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') return 'blocked'
        if (error.name === 'AbortError') return 'aborted'
    }
    return 'failed'
}

/** VideoSoundButton marks itself so the tap on it reaches its click, which turns the sound on itself. */
function isSoundButton(target: EventTarget | null): boolean {
    const element = target as Element | null

    return typeof element?.closest === 'function' && element.closest('[data-video-sound-button]') !== null
}

/**
 * Labels ride on an open page-load transaction, which is always sent. Any other open
 * transaction is not safe: the agent drops a managed click or route-change that has no
 * spans, so the label would vanish. Those cases get a transaction of their own instead,
 * which page-load must not, since ending an unmanaged transaction stops the agent's
 * performance recorder and would cut page-load's LCP and long-task capture short.
 */
function createApmReporter(source: string) {
    let agent: typeof apm | undefined
    const pending: Array<(agent: typeof apm) => void> = []

    onApmReady((ready) => {
        agent = ready
        pending.splice(0).forEach(send => send(ready))
    })

    return (labels: ApmLabels, startedAt: number): void => {
        const all = { ...labels, video_autoplay_source: source }
        const endedAt = Math.max(performance.now(), startedAt + 1)
        const send = (ready: typeof apm) => {
            const current = ready.getCurrentTransaction?.()
            if (current?.type === 'page-load') {
                current.addLabels(all)
                return
            }

            const transaction = ready.startTransaction?.(source, 'video-autoplay', { startTime: startedAt })
            transaction?.addLabels(all)
            transaction?.end(endedAt)
        }

        if (agent) send(agent)
        else pending.push(send)
    }
}

function hasSource(el: HTMLVideoElement): boolean {
    return el.hasAttribute('src') || Boolean(el.currentSrc) || el.querySelector('source') !== null
}
